/** Shared chart container — wraps every chart type */

import React, { useCallback, useRef } from "react";
import type { ChartRenderContext, ChartData } from "./types";
import { useChartAnimation } from "./useChartAnimation";
import { chartCssWidth, px } from "./chartUtils";
import { E } from "./chartColors";
import { useIsPc } from "@hooks/useIsPc";
interface ChartContainerProps {
  ctx: ChartRenderContext;
  chartData: ChartData;
  /** Override container width (default 960) */
  width?: number;
  /** Override inner flex alignment (default flex-start) */
  align?: "center" | "flex-start" | "stretch";
  /** Override container styles for diagram/infographic reuse */
  containerStyle?: React.CSSProperties;
  /** Skip animation (static diagrams) */
  disableAnimation?: boolean;
  children: React.ReactNode;
}

/**
 * Programmatic horizontal scroll for overflow:hidden containers.
 * Uses overflow:hidden so the element is NOT a CSS scroll container —
 * vertical wheel/touch events pass through to the chat page.
 * Horizontal gestures (trackpad / touch swipe) are handled manually.
 */
function useHorizontalScroll() {
  const ref = useRef<HTMLDivElement>(null);
  const touchState = useRef({ startX: 0, scrollLeft: 0, locked: false });

  const onWheel = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      return;
    }
    if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
      el.scrollLeft = Math.max(
        0,
        Math.min(maxScroll, el.scrollLeft + e.deltaX),
      );
    }
  }, []);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const el = ref.current;
    if (!el) {
      return;
    }
    touchState.current = {
      startX: e.touches[0].clientX,
      scrollLeft: el.scrollLeft,
      locked: false,
    };
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const el = ref.current;
    if (!el) {
      return;
    }
    const maxScroll = el.scrollWidth - el.clientWidth;
    if (maxScroll <= 0) {
      return;
    }
    const dx = touchState.current.startX - e.touches[0].clientX;
    if (!touchState.current.locked && Math.abs(dx) > 8) {
      touchState.current.locked = true;
    }
    if (touchState.current.locked) {
      el.scrollLeft = Math.max(
        0,
        Math.min(maxScroll, touchState.current.scrollLeft + dx),
      );
    }
  }, []);

  return { ref, onWheel, onTouchStart, onTouchMove };
}

export const ChartContainer: React.FC<ChartContainerProps> = ({
  ctx,
  chartData,
  width = 960,
  align = "flex-start",
  containerStyle,
  disableAnimation = false,
  children,
}) => {
  const { S } = ctx;
  const [isPC] = useIsPc();
  const anim = useChartAnimation(ctx, chartData);
  const widthPx = chartCssWidth(width, S, isPC);

  const opacity = disableAnimation ? 1 : anim.chartOpacity;
  const scale = disableAnimation ? 1 : anim.chartScale;

  const h = ctx.compositionHeight || 1920;
  const isShort = h >= 1800;
  const topPad = isShort ? 80 : 60;
  const bottomPad = isShort ? 300 : 60;

  const embed = ctx.embed === true;
  const scroll = useHorizontalScroll();

  if (embed) {
    return (
      <div
        className="relative flex flex-col items-center justify-center w-full"
        style={containerStyle}
      >
        <div
          ref={scroll.ref}
          className="flex flex-col items-start w-full"
          style={{
            backgroundColor: E.bgPage,
            borderRadius: 8,
            opacity,
            transform: `scale(${scale})`,
            overflow: "hidden",
          }}
          onWheel={scroll.onWheel}
          onTouchStart={scroll.onTouchStart}
          onTouchMove={scroll.onTouchMove}
        >
          <div
            style={{
              width: "100%",
              maxWidth: widthPx,
              margin: "0 auto",
              display: "flex",
              flexDirection: "column",
              alignItems: align,
            }}
          >
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "absolute",
        top: px(topPad, S),
        left: 0,
        right: 0,
        bottom: px(bottomPad, S),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 40,
        ...containerStyle,
      }}
    >
      <div
        style={{
          width: widthPx,
          backgroundColor: E.bgPage,
          borderRadius: px(24, S),
          padding: `${px(48, S)}px ${px(48, S)}px ${px(56, S)}px`,
          backdropFilter: `blur(${px(24, S)}px)`,
          border: `1.5px solid ${E.borderSubtle}`,
          opacity,
          transform: `scale(${scale})`,
          display: "flex",
          flexDirection: "column",
          alignItems: align,
        }}
      >
        {children}
      </div>
    </div>
  );
};
